import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Sidebar,
  BatchItem,
  ProgressBar,
  IconWrapper,
  BatchCurrentStatus,
  NoRecord,
} from './Styles';
import { DeniedSVGIcon, TickSVG, WarningIcon } from '../../../images/svgIcons';
import { Pagination, Skeleton, Empty, Tooltip } from 'antd';
import StatusMonitoringInfo from './StatusMonitoringInfo';
import { useSubscription } from '@apollo/client';
import { FETCH_BATCHES } from '../gql/FetchBatches';
import moment from 'moment';
import { getProgressPercentage, getStatusColor, getStatusMessage } from '../../../utils/helper';
import { withTranslation } from 'react-i18next';

const NAMESPACES = ['STATUS_MONITORING'];
const SIDEBAR_PAGINATION_HEIGHT = 52;
const BATCH_CARD_HEIGHT = 110;

const BatchCard = ({ batchData, activeItem, setActiveItem, index = 1, t }) => {
  const cardRef = useRef(null);

  const onBatchCardClick = useCallback(
    e => {
      const ref = e.currentTarget;
      cardRef.current.classList.add('clicking');
      setTimeout(() => {
        cardRef.current.classList.remove('clicking');
        setTimeout(() => {
          setActiveItem(batchData?.id);
        }, 0);
      }, 100);
    },
    [batchData],
  );

  const memoizedBatchCard = useMemo(() => {
    const { id, batchName, createdTime, totalCount, batchStats, status } = batchData;
    const percentage = getProgressPercentage(totalCount, batchStats);

    return (
      <BatchItem
        key={id}
        ref={cardRef}
        className={'animate'}
        index={index}
        active={id === activeItem}
        onClick={onBatchCardClick}
      >
        <div className="batch-title">
          <h6 title={batchName}>{batchName}</h6>
          <div>
            <p>{moment(createdTime).format('Do MMM YYYY')}</p>
            <p>{moment(createdTime).format('LT')}</p>
          </div>
        </div>   
          <div className="batch-content">
          {status === 'in_progress' || status === 'processing_complete' ? (
            <div className="progress-bar">
              <ProgressBar value={percentage}>
                <div></div>
              </ProgressBar>
              <strong>{percentage}%</strong>
            </div>
          ) : (
            getStatusMessage(status, t) && (
              <BatchCurrentStatus bg={getStatusColor(status)} status={status}>
                {getStatusMessage(status, t)}
              </BatchCurrentStatus>
            )
          )}
          {status === 'processing_complete' && (
              <Tooltip placement='top' title={getStatusMessage(status, t)}>
                <IconWrapper background={'#C8E066'}>
                  <TickSVG />
                </IconWrapper>
              </Tooltip>
            )}
            {status.includes('reject_by_level_') && (
              <Tooltip placement='top' title={getStatusMessage(status, t)}>
                <span style={{ display: 'inline-block' }}>
                  <WarningIcon height={25} width={25} />
                </span>
              </Tooltip>
            )}
          </div>
      </BatchItem>
    );
  }, [batchData, activeItem, t]);
  return <>{memoizedBatchCard}</>;
};

const StatusMonitoringContent = ({ filters, setFilters, cardCount, t, users }) => {
  const [total, setTotal] = useState(0);

  const [activeItem, setActiveItem] = useState(null);
  const [subscribed, setSubscribed] = useState(true);

  const handlePageChange = page => {
    setFilters(prevFilter => ({ filter: { ...prevFilter.filter, page: page } }));
    setTimeout(() => {
      setActiveItem(null);
    }, 1000);
  };

  const { data, loading, error } = useSubscription(FETCH_BATCHES, {
    variables: filters,
    skip: !subscribed,
    shouldResubscribe: true,
  });

  useEffect(() => {
    if (activeItem === null) {
      setActiveItem(data?.batchDocumentStats?.data ? data?.batchDocumentStats?.data[0]?.id : null);
    }
    if (data?.batchDocumentStats?.statusCode === 4005) {
      setTotal(data?.batchDocumentStats?.dataCount);
    }
  }, [data, activeItem]);

  useEffect(() => {
    activeItem && setActiveItem(null);
  }, [filters]);
  // if (error) return <p>Error: {error.message}</p>;
  return (
    <>
      {data?.batchDocumentStats?.data?.length !== 0 &&
      data?.batchDocumentStats?.statusCode !== 4006 ? (
        <>
          <Sidebar pageNumberHeight={SIDEBAR_PAGINATION_HEIGHT}>
            <>
              {data?.batchDocumentStats?.statusCode === 4005 &&
                data?.batchDocumentStats?.data?.map((batch, index) => (
                  <BatchCard
                    batchData={batch}
                    activeItem={activeItem}
                    setActiveItem={setActiveItem}
                    key={batch.id}
                    index={index}
                    t={t}
                  />
                ))}
              <div className="pagination-wrapper">
                <Pagination
                  size={'small'}
                  defaultCurrent={1}
                  total={total}
                  pageSize={cardCount}
                  onChange={e => handlePageChange(e)}
                  current={filters?.filter?.page}
                />
              </div>
            </>
          </Sidebar>
          <StatusMonitoringInfo
            jobId={activeItem}
            users={users}
            subscribed={subscribed}
            filters={filters}
          />
        </>
      ) : (
        <NoRecord>
          <Empty description={'No Jobs Found'} />
        </NoRecord>
      )}
    </>
  );
};

export default withTranslation(NAMESPACES)(StatusMonitoringContent);
